import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { normalizeWizardTeamContext } from '../../utils/playerTeamWorkspaceHelp';
import { FLOW_ROOT } from './flows/helpFlows';

const HelpWizardContext = createContext(null);

export function HelpWizardProvider({ children, rosterTeams = [], initialTeamContext = null }) {
  const [nodeId, setNodeId] = useState(FLOW_ROOT);
  const [history, setHistory] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);

  const defaultTeamCtx = useMemo(() => {
    if (initialTeamContext?.teamId) return initialTeamContext;
    return null;
  }, [initialTeamContext]);

  const applyDefaultTeam = useCallback(() => {
    if (defaultTeamCtx) {
      setSelectedTeam(defaultTeamCtx);
    } else {
      setSelectedTeam(null);
    }
  }, [defaultTeamCtx]);

  /** Reset flow state (e.g. when leaving /player/help or after an action). */
  const resetWizard = useCallback(() => {
    setNodeId(FLOW_ROOT);
    setHistory([]);
    setSelectedMatch(null);
    applyDefaultTeam();
  }, [applyDefaultTeam]);

  const goToNode = useCallback((nextId, { pushHistory = true } = {}) => {
    setSelectedMatch(null);
    setNodeId((current) => {
      if (pushHistory) {
        setHistory((h) => [...h, current]);
      }
      return nextId;
    });
  }, []);

  const goBack = useCallback(() => {
    setSelectedMatch(null);
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setNodeId(prev);
      return h.slice(0, -1);
    });
  }, []);

  const startOver = useCallback(() => {
    setNodeId(FLOW_ROOT);
    setHistory([]);
    setSelectedMatch(null);
    applyDefaultTeam();
  }, [applyDefaultTeam]);

  const pickTeamFromRoster = useCallback((teamRow) => {
    const ctx = normalizeWizardTeamContext(teamRow);
    setSelectedTeam(
      ctx
        ? {
            ...ctx,
            rosterRow: teamRow,
          }
        : null
    );
  }, []);

  const value = useMemo(
    () => ({
      nodeId,
      history,
      selectedTeam,
      selectedMatch,
      setSelectedMatch,
      rosterTeams,
      resetWizard,
      goToNode,
      goBack,
      startOver,
      pickTeamFromRoster,
    }),
    [
      nodeId,
      history,
      selectedTeam,
      selectedMatch,
      rosterTeams,
      resetWizard,
      goToNode,
      goBack,
      startOver,
      pickTeamFromRoster,
    ]
  );

  return <HelpWizardContext.Provider value={value}>{children}</HelpWizardContext.Provider>;
}

export function useHelpWizard() {
  const ctx = useContext(HelpWizardContext);
  if (!ctx) {
    throw new Error('useHelpWizard must be used within HelpWizardProvider');
  }
  return ctx;
}

export function useHelpWizardOptional() {
  return useContext(HelpWizardContext);
}
